import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { registerUser, selectSignUpState, resetSignUpState } from "../../../redux/WithcookieSlice";
import axios from "axios";
import { showToast } from "../../common/Toast";
import { useTranslation } from "../../../redux/TranslationHelper";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { SignupProps, SignupFormData, VarFieldConfig } from "./Signup.types";
import { adaptSignupProps } from "./Signup.adapter";
import './Signup.css';

// memoized 버전의 컴포넌트 생성
const SignupComponent: React.FC<any> = React.memo((props) => {
  // 어댑터를 통해 props 변환
  // 매번 새로운 props를 생성하지 않도록 useMemo 사용
  const adaptedProps = useMemo(() => adaptSignupProps(props), [props]);
  
  // 렌더 카운트 추적을 위한 ref - 조건부 호출이 아닌 컴포넌트 최상위 레벨에서 선언
  const renderCountRef = React.useRef(0);
  
  // 디버깅 로그 출력 
  useEffect(() => {
    renderCountRef.current++;

  });
  
  const {
    style = {},
    className = '',
    content = {},
    data = {},
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    componentProps = {},
    pathVariables = {},
    varFields = {
      var01: { 
        show: false, 
        label: "", 
        type: "input", 
        options: [], 
        required: false,
        content: ""
      },
      var02: { 
        show: false, 
        label: "", 
        type: "input", 
        options: [], 
        required: false,
        content: ""
      },
      var03: { 
        show: false, 
        label: "", 
        type: "input", 
        options: [], 
        required: false,      
        content: ""
      },
      var04: { 
        show: false, 
        label: "", 
        type: "input", 
        options: [], 
        required: false,
        content: ""
      },
      var05: { 
        show: false, 
        label: "", 
        type: "input", 
        options: [], 
        required: false,
        content: ""
      },
      var06: { 
        show: false, 
        label: "", 
        type: "input", 
        options: [], 
        required: false,
        content: ""
      },
      var07: { 
        show: false, 
        label: "", 
        type: "input", 
        options: [], 
        required: false,
        content: ""
      },
      var08: { 
        show: false, 
        label: "", 
        type: "input", 
        options: [], 
        required: false,
        content: ""
      },
      var09: { 
        show: false, 
        label: "", 
        type: "input", 
        options: [], 
        required: false,
        content: ""
      },
      var10: { 
        show: false, 
        label: "", 
        type: "input", 
        options: [], 
        required: false,
        content: ""
      }
    },
    basicFields = {
      userId: true,
      password: true,
      name: true,
      phone: true,
      email: true,
      birthday: true,
      address: true,
      referralCode: true
    },
    ...rest
  } = adaptedProps;

  // Redux 타입 오류 해결을 위한 any 타입 단언
  const dispatch = useDispatch() as any;
  const { t } = useTranslation();
  

  // useParams() 대신 props로 받은 데이터 사용
  const referralCodeFromProps = pathVariables.referralCode || content.referralCode || data.referralCode;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loading, signUpSuccess, signUpError, validationErrors: reduxValidationErrors } = useSelector(selectSignUpState);
  
  // check_current API에서 직접 데이터 가져오기
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [checkCurrentData, setCheckCurrentData] = useState<any>(null);
  const [directReferralCode, setDirectReferralCode] = useState("");

  // withcookieData에서 테마 정보 가져오기
  const withcookieData = useSelector((state: any) => state.withcookie.withcookieData);
  
  // 테마 색상 설정
  const theme = withcookieData?.skin?.theme || {};
  const colorSet = theme?.colorset || {};
  const primaryColor = colorSet?.primary || "#007bff";
  const secondaryColor = colorSet?.secondary || "#6c757d";

  // 기존 skinData 선택자는 유지 (다른 목적으로 사용될 수 있음)
  const skinData = useSelector((state: any) => {
    // Redux 상태 구조에 따라 skin 데이터 추출 방법 변경
    // 여러 가능한 경로 확인
    if (state.withcookie?.skin) return state.withcookie.skin;
    if (state.skin) return state.skin;
    // 전체 state에서 skin 속성 찾기
    for (const key in state) {
      if (state[key]?.skin) return state[key].skin;
    }
    return {};
  }, (left: any, right: any) => {
    // shallow equality check for memoization
    return JSON.stringify(left) === JSON.stringify(right);
  });
  
  // 참조 경로 수정: skin 데이터 구조에 맞게 referralCode 접근
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const skinReferralCode = skinData?.referralCode || "";
  
  // 로컬 상태 관리
  const [formData, setFormData] = useState<SignupFormData>({
    user_id: "",
    password: "",
    phone: "",
    name: "",
    referral_code: "",
    email: "",
    birthday: "",
    address: "",
    upperer_code: "",
    center_code: "",
    left_or_right: "0",
    var01: "",
    var02: "",
    var03: "",
    var04: "",
    var05: "",
    var06: "",
    var07: "",
    var08: "",
    var09: "",
    var10: "",
    referral_code_from_url: false,
    trxWithdrawAddress: "",
    bscWithdrawAddress: ""
  });
  
  // useRef를 사용하여 초기화 여부 추적
  const formDataInitialized = useRef(false);
  
  const [localValidationErrors, setLocalValidationErrors] = useState<Record<string, string>>({});
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [policies, setPolicies] = useState<any>(null);
  const [isSpecifiedLegPolicy, setIsSpecifiedLegPolicy] = useState(false);
  const [isCheckCurrentLoading, setIsCheckCurrentLoading] = useState(false);
  const [isPoliciesLoading, setIsPoliciesLoading] = useState(false);

  // API 호출 중복 방지 ref
  const checkCurrentCallRef = useRef(false);
  
  // check_current API 직접 호출
  useEffect(() => {
    // 이미 로딩 중이거나 데이터가 있거나 호출 진행 중이면 스킵
    if (isCheckCurrentLoading || checkCurrentData || checkCurrentCallRef.current) return;
    
    const fetchCheckCurrent = async () => {
      checkCurrentCallRef.current = true;
      setIsCheckCurrentLoading(true);
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
        const response = await axios.get(`${apiBaseUrl}/api/check_current`, {
          withCredentials: true
        });
        
        setCheckCurrentData(response.data);
        
        // skin.referralCode 값 추출
        if ((response.data as any)?.skin?.referralCode) {
          setDirectReferralCode((response.data as any).skin.referralCode);
        }
      } catch (error) {
        console.error(t('check_current API 호출 오류:'), error);
      } finally {
        setIsCheckCurrentLoading(false);
        checkCurrentCallRef.current = false;
      }
    };
    
    fetchCheckCurrent();
  }, [t, isCheckCurrentLoading, checkCurrentData]);

  // 리덕스의 validation 에러와 로컬 에러 병합
  const validationErrors = { ...localValidationErrors, ...(reduxValidationErrors || {}) };

  // 추천인 코드가 있으면 폼 데이터에 설정 (우선순위: API > props > URL)
  useEffect(() => {
    // 1. API에서 직접 가져온 referralCode가 있으면 최우선으로 사용
    if (directReferralCode && directReferralCode.trim() !== "") {
      setFormData(prevData => ({
        ...prevData,
        referral_code: directReferralCode,
        referral_code_from_url: true // 수정 불가능하게 처리
      }));
    }
    // 2. props로 받은 코드가 있으면 사용
    else if (referralCodeFromProps) {
      setFormData(prevData => ({
        ...prevData,
        referral_code: referralCodeFromProps,
        referral_code_from_url: true
      }));
    } 
    // 3. URL에서 직접 추출 시도 (fallback 방식)
    else {
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      if (pathSegments.length >= 2 && pathSegments[0] === 'signup') {
        const codeFromUrl = pathSegments[1];
        setFormData(prevData => ({
          ...prevData,
          referral_code: codeFromUrl,
          referral_code_from_url: true
        }));
      }
    }
  }, [referralCodeFromProps, directReferralCode]);

  // 컴포넌트 언마운트 시 회원가입 상태 초기화
  useEffect(() => {
    return () => {
      dispatch(resetSignUpState());
    };
  }, [dispatch]);

  // 정책 API 호출 중복 방지 ref
  const policiesCallRef = useRef(false);
  
  // 컴포넌트 마운트 시 정책 데이터 불러오기
  useEffect(() => {
    // 이미 로딩 중이거나 데이터가 있거나 호출 진행 중이면 스킵
    if (isPoliciesLoading || policies || policiesCallRef.current) return;
    
    const fetchPolicies = async () => {
      policiesCallRef.current = true;
      setIsPoliciesLoading(true);
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
        const response = await axios.get(`${apiBaseUrl}/api/policies/all`);
        setPolicies(response.data);
        
        // SPECIFIED 타입의 legPolicy가 있는지 확인
        const responseData = response.data as any;
        if (responseData && responseData.legPolicies && responseData.legPolicies.length > 0) {
          const hasSpecifiedLegPolicy = responseData.legPolicies.some((policy: any) => policy.type === 'SPECIFIED');
          setIsSpecifiedLegPolicy(hasSpecifiedLegPolicy);
        }
      } catch (error) {
        console.error(t('정책 로드 중 오류 발생:'), error);
      } finally {
        setIsPoliciesLoading(false);
        policiesCallRef.current = false;
      }
    };
    
    fetchPolicies();
  }, [t, isPoliciesLoading, policies]);

  // varFields가 변경될 때마다 해당 필드의 초기값을 formData에 설정
  useEffect(() => {
    
    // 한 번만 초기화
    if (!formDataInitialized.current) {
      // 특정 필드의 기본값 설정
      const initialFieldValues = {
        var01: '',
        var02: '',
        var03: '',
        var04: '',
        var05: '',
        var06: '',
        var07: '',
        var08: '',
        var09: '',
        var10: ''
      };
      
      // formData 업데이트
      setFormData(prevData => ({
        ...prevData,
        ...initialFieldValues
      }));
      
      formDataInitialized.current = true;
    }
    
  }, []); // 컴포넌트 마운트 시에만 실행 - 의존성 배열 비움

  // 회원가입 성공 시 홈페이지로 리다이렉트
  useEffect(() => {
    if (signUpSuccess) {
      const redirectTimer = setTimeout(() => {
        window.location.href = '/';
      }, 3000); // 3초 후 리다이렉트
      
      return () => clearTimeout(redirectTimer);
    }
  }, [signUpSuccess]);

  // 스타일 필터링 - 패딩 관련 속성 제거 (CSS 클래스에서 정의)
  const filteredStyle = {...style};
  delete filteredStyle.padding;
  delete filteredStyle.paddingLeft;
  delete filteredStyle.paddingRight;
  delete filteredStyle.paddingTop;
  delete filteredStyle.paddingBottom;
  
  // 필드별 유효성 검사
  const validateField = React.useCallback((id: string, value: string) => {
    let errors = {...localValidationErrors};
    
    switch(id) {
      case 'user_id':
        if (!value.trim()) {
          errors.user_id = t('ID를 입력해주세요');
        } else if (value.length < 2 || value.length > 20) {
          errors.user_id = t('ID는 2~20자로 입력해주세요');
        } else {
          delete errors.user_id;
        }
        break;
        
      case 'password':
        if (!value) {
          errors.password = t('비밀번호를 입력해주세요');
        } 
        else {
          delete errors.password;
        }
        break;
        
      case 'phone':
        // 핸드폰 번호 검증 (숫자만 필수)
        if (!value) {
          errors.phone = t('핸드폰 번호를 입력해주세요');
        } else {
          delete errors.phone;
        }
        break;
        
      case 'name':
        if (!value.trim()) {
          errors.name = t('이름을 입력해주세요');
        } else if (!/^[가-힣a-zA-Z0-9_-]+$/.test(value)) {
          errors.name = t('이름은 한글, 영문, 숫자, _, - 만 사용 가능합니다');
        } else {
          delete errors.name;
        }
        break;
        
      case 'email':
        if (value && !/\S+@\S+\.\S+/.test(value)) {
          errors.email = t('유효한 이메일 형식이 아닙니다');
        } else {
          delete errors.email;
        }
        break;
        
      // 추천인 코드와 기타 선택적 필드는 특별한 유효성 검사 없음
      default:
        delete errors[id];
        break;
    }
    
    setLocalValidationErrors(errors);
  }, [localValidationErrors, t]);
  
  // 입력값 변경 핸들러
  const handleChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    
    // URL에서 referralCode가 설정된 경우에만 referral_code 필드 변경 방지
    if (id === 'referral_code' && formData.referral_code_from_url) {
      return;
    }
    
    setFormData(prevData => ({
      ...prevData,
      [id]: (e.target as HTMLInputElement).type === 'radio' ? value : value
    }));

    // 유효성 검사
    validateField(id, value);
  }, [formData.referral_code_from_url, validateField]);
  
  // 폼 전체 유효성 검사
  const validateForm = () => {
    const errors: Record<string, string> = {};
    const { user_id, password, phone, name, email } = formData;
    
    // 아이디 검사
    if (!user_id.trim()) {
      errors.user_id = t('ID를 입력해주세요');
    } else if (user_id.length < 2 || user_id.length > 20) {
      errors.user_id = t('ID는 2~20자로 입력해주세요');
    }
    
    // 비밀번호 검사
    if (!password) {
      errors.password = t('비밀번호를 입력해주세요');
    }
    
    // 핸드폰 번호 검사
    if (!phone) {
      errors.phone = t('핸드폰 번호를 입력해주세요');
    }
    
    // 이름 검사
    if (!name.trim()) {
      errors.name = t('이름을 입력해주세요');
    } else if (!/^[가-힣a-zA-Z0-9_-]+$/.test(name)) {
      errors.name = t('이름은 한글, 영문, 숫자, _, - 만 사용 가능합니다');
    }
    
    // 이메일 검사 (값이 있을 경우만)
    if (email && !/\S+@\S+\.\S+/.test(email)) {
      errors.email = t('유효한 이메일 형식이 아닙니다');
    }
    
    // 필수 동의 항목 및 특수 필드 검사
    Object.entries(varFields).forEach(([fieldKey, fieldConfig]: any) => {
      const fieldValue = formData[fieldKey as keyof SignupFormData];
      
      if (fieldConfig.show && fieldConfig.required) {
        if (fieldConfig.type === 'document' && !fieldValue) {
          errors[fieldKey] = `${fieldConfig.label}을 동의하셔야 합니다.`;
        } else if (fieldConfig.type === 'account') {
          const [bankCode, accountNumber] = (fieldValue || '').toString().split('|');
          if (!bankCode || !accountNumber) {
            errors[fieldKey] = `${fieldConfig.label}을 올바르게 입력해주세요.`;
          }
        } else if (fieldConfig.type === 'ssn') {
          const [ssnFront, ssnBack] = (fieldValue || '').toString().split('-');
          if (!ssnFront || ssnFront.length !== 6 || !ssnBack || ssnBack.length !== 7) {
            errors[fieldKey] = `${fieldConfig.label}을 올바른 형식으로 입력해주세요.`;
          }
        } else if (!fieldValue) {
          errors[fieldKey] = `${fieldConfig.label}을 입력해주세요.`;
        }
      }
    });
    
    setLocalValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 유효성 검사
    if (!validateForm()) {
      return;
    }
    
    // API 전송 데이터 구성
    const apiData: any = {
      user_id: formData.user_id,
      password: formData.password,
      name: formData.name,
      phone: formData.phone,
      referral_code: formData.referral_code,
      email: formData.email,
      birthday: formData.birthday,
      address: formData.address,
      var01: formData.var01,
      var02: formData.var02,
      var03: formData.var03,
      var04: formData.var04,
      var05: formData.var05,
      var06: formData.var06,
      var07: formData.var07,
      var08: formData.var08,
      var09: formData.var09,
      var10: formData.var10,
      center_code: formData.center_code
    };
    
    // SPECIFIED 정책일 경우 추가 데이터 포함
    if (isSpecifiedLegPolicy) {
      apiData.upperer_code = formData.upperer_code;
      apiData.left_or_right = formData.left_or_right;
    }
    
    // company.id가 24인 경우 지갑 주소 포함
    if (withcookieData?.skin?.companyId === 24) {
      apiData.trxWithdrawAddress = formData.trxWithdrawAddress;
      apiData.bscWithdrawAddress = formData.bscWithdrawAddress;
    }
    
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const result = await dispatch(registerUser(apiData)).unwrap();
      // 성공 시 토스트 메시지 표시
      showToast(t("회원가입이 완료되었습니다!"), "success");
    } catch (error: any) {
      // 에러 메시지 토스트로 표시
      const errorMessage = error.message || t("회원가입에 실패했습니다.");
      showToast(errorMessage, "error");
      
      // 개발 환경에서 디버깅용 콘솔 로그
      console.error(t('[회원가입 에러]'), error);
    }
  };

  // 라디오 버튼 핸들러
  const handleRadioChange = React.useCallback((fieldName: string, value: string) => {
    setFormData(prevData => ({
      ...prevData,
      [fieldName]: value
    }));
  }, []);
  
  // 체크박스 핸들러 
  const handleCheckboxChange = React.useCallback((fieldName: string, label: string, isChecked: boolean) => {
    setFormData(prevData => ({
      ...prevData,
      [fieldName]: isChecked ? `${label} 동의함` : ''
    }));
  }, []);

  // var 필드 렌더링 함수
  const renderVarField = React.useCallback((fieldName: string, fieldConfig: VarFieldConfig, styleOverride: React.CSSProperties = {}) => {
    if (!fieldConfig || !fieldConfig.show) {
      return null;
    }

    // 계좌번호와 주민등록번호 필드의 값을 분리하여 관리하기 위한 상태
    const fieldValue = formData[fieldName as keyof SignupFormData]?.toString() || '';
    const [bankCode = '', accountNumber = ''] = fieldValue.includes('|') ? fieldValue.split('|') : ['', ''];
    const [ssnFront = '', ssnBack = ''] = fieldValue.includes('-') ? fieldValue.split('-') : ['', ''];

    switch (fieldConfig.type) {
      case 'select':
        return (
          <div className="signup-form-group" style={styleOverride} key={fieldName}>
            <label htmlFor={fieldName} className="signup-label">
              {fieldConfig.required ? '* ' : ''}{fieldConfig.label}
            </label>
            <select
              id={fieldName}
              className="signup-input"
              value={formData[fieldName as keyof SignupFormData] as string}
              onChange={handleChange}
              required={fieldConfig.required}
            >
              <option value="">{t('선택하세요')}</option>
              {fieldConfig.options && fieldConfig.options.map((option: any, index: number) => {
         
                
                // value가 없는 경우 label을 value로 사용
                const optionValue = option.value || option.label;
                
                return (
                  <option key={index} value={optionValue}>
                    {option.label}
                  </option>
                );
              })}
            </select>
            {validationErrors[fieldName] && (
              <div className="signup-error-message">
                {validationErrors[fieldName]}
              </div>
            )}
          </div>
        );
      
      case 'radio':
        return (
          <div className="signup-form-group" style={styleOverride} key={fieldName}>
            <label className="signup-label" style={{marginBottom: '10px'}}>
              {fieldConfig.required ? '* ' : ''}{fieldConfig.label}
            </label>
            <div className="signup-radio-group">
              {fieldConfig.options && fieldConfig.options.map((option: any, index: number) => {
              
                return (
                <div className="signup-radio-item" key={index}>
                  <input 
                    type="radio" 
                    id={`${fieldName}_${index}`}
                    name={fieldName}
                    value={option.value}
                    checked={formData[fieldName as keyof SignupFormData] === option.value}
                    onChange={() => handleRadioChange(fieldName, option.value)}
                    required={fieldConfig.required}
                    className="signup-radio"
                  />
                  <label htmlFor={`${fieldName}_${index}`} className="signup-radio-label">
                    {option.label}
                  </label>
                </div>
                );
              })}
            </div>
            {validationErrors[fieldName] && (
              <div className="signup-error-message">
                {validationErrors[fieldName]}
              </div>
            )}
          </div>
        );
      
      case 'document':
        return (
          <div className="signup-form-group" style={styleOverride} key={fieldName}>
            <label className="signup-label" style={{marginBottom: '10px'}}>
              {fieldConfig.required ? '* 필수 : ' : ''}{fieldConfig.label} 
            </label>
            <div className="signup-checkbox-container">
              <input 
                type="checkbox" 
                id={fieldName} 
                checked={formData[fieldName as keyof SignupFormData] ? true : false} 
                onChange={(e) => handleCheckboxChange(fieldName, fieldConfig.label, e.target.checked)}
                className="signup-checkbox"
                required={fieldConfig.required}
              />
              <label htmlFor={fieldName} className="signup-checkbox-label">
                동의합니다
              </label>
            </div>
            <div style={{position: 'relative'}}>
              <textarea 
                className="signup-textarea"
                value={fieldConfig.content || ''}
                readOnly
              />
            </div>
            {validationErrors[fieldName] && (
              <div className="signup-error-message">
                {validationErrors[fieldName]}
              </div>
            )}
          </div>
        );
      
      case 'account':
        return (
          <div className="signup-form-group" style={styleOverride} key={fieldName}>
            <label className="signup-label">
              {fieldConfig.required ? '* ' : ''}{fieldConfig.label}
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <select
                className="signup-input"
                style={{ flex: '0 0 150px' }}
                value={bankCode}
                onChange={(e) => {
                  const newValue = `${e.target.value}|${accountNumber || ''}`;
                  setFormData(prevData => ({
                    ...prevData,
                    [fieldName]: newValue
                  }));
                  validateField(fieldName, newValue);
                }}
                required={fieldConfig.required}
              >
                <option value="">{t('은행 선택')}</option>
                <option value="KB">{t('KB국민은행')}</option>
                <option value="SH">{t('신한은행')}</option>
                <option value="WR">{t('우리은행')}</option>
                <option value="HN">{t('하나은행')}</option>
                <option value="NH">{t('NH농협은행')}</option>
                <option value="KP">{t('우체국')}</option>
                <option value="IBK">{t('IBK기업은행')}</option>
                <option value="KDB">{t('KDB산업은행')}</option>
                <option value="SC">{t('SC제일은행')}</option>
                <option value="CT">{t('씨티은행')}</option>
                <option value="DG">{t('대구은행')}</option>
                <option value="BS">{t('부산은행')}</option>
                <option value="GJ">{t('광주은행')}</option>
                <option value="JJ">{t('제주은행')}</option>
                <option value="JB">{t('전북은행')}</option>
                <option value="KN">{t('경남은행')}</option>
                <option value="KK">{t('카카오뱅크')}</option>
                <option value="TS">{t('토스뱅크')}</option>
              </select>
              <input
                type="text"
                className="signup-input"
                style={{ flex: '1' }}
                placeholder={t('계좌번호 입력')}
                value={accountNumber}
                onChange={(e) => {
                  const newValue = `${bankCode || ''}|${e.target.value}`;
                  setFormData(prevData => ({
                    ...prevData,
                    [fieldName]: newValue
                  }));
                  validateField(fieldName, newValue);
                }}
                required={fieldConfig.required}
              />
            </div>
            {validationErrors[fieldName] && (
              <div className="signup-error-message">
                {validationErrors[fieldName]}
              </div>
            )}
          </div>
        );
      
      case 'ssn':
        return (
          <div className="signup-form-group" style={styleOverride} key={fieldName}>
            <label className="signup-label">
              {fieldConfig.required ? '* ' : ''}{fieldConfig.label}
            </label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="text"
                className="signup-input"
                style={{ flex: '1' }}
                placeholder={t('앞자리 (6자리)')}
                maxLength={6}
                value={ssnFront}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  const newValue = `${value}-${ssnBack || ''}`;
                  setFormData(prevData => ({
                    ...prevData,
                    [fieldName]: newValue
                  }));
                  validateField(fieldName, newValue);
                }}
                required={fieldConfig.required}
              />
              <span style={{ fontSize: '20px', color: '#666' }}>-</span>
              <input
                type="password"
                className="signup-input"
                style={{ flex: '1' }}
                placeholder={t('뒷자리 (7자리)')}
                maxLength={7}
                value={ssnBack}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  const newValue = `${ssnFront || ''}-${value}`;
                  setFormData(prevData => ({
                    ...prevData,
                    [fieldName]: newValue
                  }));
                  validateField(fieldName, newValue);
                }}
                required={fieldConfig.required}
              />
            </div>
            {validationErrors[fieldName] && (
              <div className="signup-error-message">
                {validationErrors[fieldName]}
              </div>
            )}
          </div>
        );
      
      default: // input
        return (
          <div className="signup-form-group" style={styleOverride} key={fieldName}>
            <label htmlFor={fieldName} className="signup-label">
              {fieldConfig.required ? '* ' : ''}{fieldConfig.label}
            </label>
            <input 
              id={fieldName}
              type="text" 
              placeholder={`${fieldConfig.label} ${t('입력')}`}
              className="signup-input"
              value={formData[fieldName as keyof SignupFormData] as string}
              onChange={handleChange}
              required={fieldConfig.required}
            />
            {validationErrors[fieldName] && (
              <div className="signup-error-message">
                {validationErrors[fieldName]}
              </div>
            )}
          </div>
        );
    }
  }, [formData, handleCheckboxChange, handleRadioChange, t, validationErrors, handleChange]);

  return (
    <div className={`signup-form ${className}`} style={filteredStyle} {...rest}>
      <h2>{t('회원가입')}</h2>
      
      {signUpSuccess ? (
        <div className="signup-success-message">
          <p>{t('회원가입이 완료되었습니다!')}</p>
          <p>{t('잠시 후 홈페이지로 이동합니다...')}</p>
        </div>
      ) : (
        <form className="signup-form-container" onSubmit={handleSubmit}>
          <div className="signup-section-title">{t('회원 정보')}</div>
          
          {/* 기본 정보 행 */}
          <div className="signup-form-row">
            {/* 아이디 입력 필드 */}
            {basicFields.userId && (
              <div className="signup-form-group">
                <label htmlFor="user_id" className="signup-label">{t('아이디')} *</label>
                <input 
                  id="user_id" 
                  type="text" 
                  placeholder={t('아이디 입력 (2~20자)')} 
                  required 
                  className={`signup-input ${validationErrors.user_id ? 'signup-input-error' : ''}`}
                  value={formData.user_id}
                  onChange={handleChange}
                  minLength={2}
                  maxLength={20}
                />
                {validationErrors.user_id && (
                  <div className="signup-error-message">
                    {validationErrors.user_id}
                  </div>
                )}
              </div>
            )}

            {/* 비밀번호 입력 필드 */}
            {basicFields.password && (
              <div className="signup-form-group">
                <label htmlFor="password" className="signup-label">{t('비밀번호')} *</label>
                <input 
                  id="password" 
                  type="password" 
                  placeholder={t('비밀번호 입력')} 
                  required 
                  className={`signup-input ${validationErrors.password ? 'signup-input-error' : ''}`}
                  value={formData.password}
                  onChange={handleChange}
                />
                {validationErrors.password && (
                  <div className="signup-error-message">
                    {validationErrors.password}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* 개인 정보 행 */}
          <div className="signup-form-row">
            {/* 이름 입력 필드 */}
            {basicFields.name && (
              <div className="signup-form-group">
                <label htmlFor="name" className="signup-label">{t('이름')} *</label>
                <input 
                  id="name" 
                  type="text" 
                  placeholder={t('이름 입력')} 
                  required 
                  className={`signup-input ${validationErrors.name ? 'signup-input-error' : ''}`}
                  value={formData.name}
                  onChange={handleChange}
                />
                {validationErrors.name && (
                  <div className="signup-error-message">
                    {validationErrors.name}
                  </div>
                )}
              </div>
            )}
            
            {/* 핸드폰 번호 입력 필드 */}
            {basicFields.phone && (
              <div className="signup-form-group">
                <label htmlFor="phone" className="signup-label">{t('핸드폰 번호')} *</label>
                <input 
                  id="phone" 
                  type="tel" 
                  placeholder={t('핸드폰 번호 입력')} 
                  required 
                  className={`signup-input ${validationErrors.phone ? 'signup-input-error' : ''}`}
                  value={formData.phone}
                  onChange={handleChange}
                />
                {validationErrors.phone && (
                  <div className="signup-error-message">
                    {validationErrors.phone}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* 추가 정보 행 */}
          {(basicFields.email || basicFields.birthday) && (
            <div className="signup-form-row">
              {/* 이메일 입력 필드 */}
              {basicFields.email && (
                <div className="signup-form-group">
                  <label htmlFor="email" className="signup-label">{t('이메일')}</label>
                  <input 
                    id="email" 
                    type="email" 
                    placeholder={t('이메일 입력')} 
                    className={`signup-input ${validationErrors.email ? 'signup-input-error' : ''}`}
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {validationErrors.email && (
                    <div className="signup-error-message">
                      {validationErrors.email}
                    </div>
                  )}
                </div>
              )}
              
              {/* 생일 입력 필드 */}
              {basicFields.birthday && (
                <div className="signup-form-group">
                  <label htmlFor="birthday" className="signup-label">{t('생년월일')}</label>
                  <input 
                    id="birthday" 
                    type="date" 
                    className="signup-input"
                    value={formData.birthday}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>
          )}
          
          {/* 주소 입력 필드 */}
          {basicFields.address && (
            <div className="signup-form-group">
              <label htmlFor="address" className="signup-label">{t('주소')}</label>
              <input 
                id="address" 
                type="text" 
                placeholder={t('주소 입력')} 
                className="signup-input"
                value={formData.address}
                onChange={handleChange}
              />
            </div>
          )}
          
          {/* 추천인 코드 필드 */}
          {basicFields.referralCode && (
            <div className="signup-form-group">
              <label htmlFor="referral_code" className="signup-label">{t('추천인 코드')}</label>
              <input 
                id="referral_code" 
                type="text" 
                placeholder={t('추천인 코드 입력')} 
                className="signup-input"
                value={formData.referral_code}
                onChange={handleChange}
                disabled={formData.referral_code_from_url}
              />
            </div>
          )}

          {/* SPECIFIED 정책일 경우만 표시되는 필드들 */}
          {isSpecifiedLegPolicy && (
            <div className="signup-form-row">
              {/* 후원인 코드 필드 */}
              <div className="signup-form-group">
                <label htmlFor="upperer_code" className="signup-label">{t('후원인 코드')}</label>
                <input 
                  id="upperer_code" 
                  type="text" 
                  placeholder={t('후원인 코드 입력')} 
                  className="signup-input"
                  value={formData.upperer_code}
                  onChange={handleChange}
                />
              </div>
              
              {/* 좌/우 선택 필드 */}
              <div className="signup-form-group">
                <label htmlFor="left_or_right" className="signup-label">{t('좌/우 선택')}</label>
                <select
                  id="left_or_right"
                  className="signup-input"
                  value={formData.left_or_right}
                  onChange={handleChange}
                >
                  <option value="0">{t('좌')}</option>
                  <option value="1">{t('우')}</option>
                </select>
              </div>
            </div>
          )}

          {/* 센터 코드 필드 (companyId==24 일 경우만 표시) */}
          {(withcookieData?.skin?.companyId === 24 || withcookieData?.skin?.companyId === 26) && (
            <div className="signup-form-row">
              <div className="signup-form-group">
                <label htmlFor="center_code" className="signup-label">{t('Leader Code')}</label>
                <input 
                  id="center_code" 
                  type="text" 
                  placeholder={t('Leader Code')} 
                  className="signup-input"
                  value={formData.center_code}
                  onChange={handleChange}
                  //필수사항
                  required
                />
              </div>
            </div>
          )}

          {/* 지갑 주소 필드 (companyId==24 일 경우만 표시) */}
          {(withcookieData?.skin?.companyId === 24 || withcookieData?.skin?.companyId === 26) && (
            <div className="signup-form-row">
              {(withcookieData?.skin?.companyId === 24) && (
                <div className="signup-form-group">
                  <label htmlFor="trxWithdrawAddress" className="signup-label">{t('TRX 지갑주소')}</label>
                  <input 
                    id="trxWithdrawAddress" 
                    type="text" 
                    placeholder={t('TRX 지갑주소 입력')} 
                    className="signup-input"
                    value={formData.trxWithdrawAddress}
                    onChange={handleChange}
                  />
                </div>
              )}

              <div className="signup-form-group">
                <label htmlFor="bscWithdrawAddress" className="signup-label">{t('USDT-BNB 지갑주소')}</label>
                <input 
                  id="bscWithdrawAddress" 
                  type="text" 
                  placeholder={t('USDT-BNB 지갑주소 입력')} 
                  className="signup-input"
                  value={formData.bscWithdrawAddress}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}

          {/* var 필드 렌더링 섹션 (커스텀 필드를 위한 섹션) */}
          {/* 커스텀 필드가 하나라도 표시되는 경우 섹션 제목 표시 */}
          {varFields && (varFields.var01?.show || varFields.var02?.show || varFields.var03?.show || 
                         varFields.var04?.show || varFields.var05?.show || varFields.var06?.show || 
                         varFields.var07?.show || varFields.var08?.show || varFields.var09?.show || 
                         varFields.var10?.show) && (
            <div className="signup-custom-fields-section">
              <div className="signup-section-title">{t('추가 정보')}</div>
              
              {/* var01 필드 (단독으로 렌더링) */}
              {varFields.var01?.show && (
                <div className="signup-form-row">
                  {renderVarField('var01', varFields.var01, { width: '100%' })}
                </div>
              )}
              
              {/* var02, var03 필드 (같은 행에 렌더링) */}
              {(varFields.var02?.show || varFields.var03?.show) && (
                <div className="signup-form-row">
                  {varFields.var02?.show && renderVarField('var02', varFields.var02)}
                  {varFields.var03?.show && renderVarField('var03', varFields.var03)}
                </div>
              )}
              
              {/* var04, var05 필드 (같은 행에 렌더링) */}
              {(varFields.var04?.show || varFields.var05?.show) && (
                <div className="signup-form-row">
                  {varFields.var04?.show && renderVarField('var04', varFields.var04)}
                  {varFields.var05?.show && renderVarField('var05', varFields.var05)}
                </div>
              )}
              
              {/* var06, var07 필드 (같은 행에 렌더링) */}
              {(varFields.var06?.show || varFields.var07?.show) && (
                <div className="signup-form-row">
                  {varFields.var06?.show && renderVarField('var06', varFields.var06)}
                  {varFields.var07?.show && renderVarField('var07', varFields.var07)}
                </div>
              )}
              
              {/* var08, var09 필드 (같은 행에 렌더링) */}
              {(varFields.var08?.show || varFields.var09?.show) && (
                <div className="signup-form-row">
                  {varFields.var08?.show && renderVarField('var08', varFields.var08)}
                  {varFields.var09?.show && renderVarField('var09', varFields.var09)}
                </div>
              )}
              
              {/* var10 필드 (단독으로 렌더링) */}
              {varFields.var10?.show && (
                <div className="signup-form-row">
                  {renderVarField('var10', varFields.var10, { width: '100%' })}
                </div>
              )}
            </div>
          )}
          


          {/* 회원가입 버튼 */}
          <button 
            type="submit" 
            className="signup-button"
            style={{
              backgroundColor: loading ? secondaryColor : primaryColor,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            disabled={loading}
          >
            {loading ? t('처리 중...') : t('회원가입')}
          </button>
        </form>
      )}
    </div>
  );
});

export default SignupComponent; 